import { Modal, Form, Input, Button, Select, Switch } from "antd";
import { useRef, useState, useEffect } from "react";
import Draggable from "react-draggable";
import { useSelector } from "react-redux";

import { formItemLayout, inputLayout } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import Loading from "../../../common/Loading";
import FormInputRules from "../../../../util/FormInputRules";
import { General } from "../../../../util/General";
import { ModuleAndIdMapping, Modules } from "../../../../constants/DatabaseConstants/MasterConstant";
import useGetOneMasterList from "../../../../hooks/useGetOneMasterList";
import styles from "../../index.module.less";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";
import { ErrorMessageConstants } from "../../../../constants/MessageConstants/ErrorMessageConstants";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import type { RootState } from "../../../../appRedux/store";

type KeyFobMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
  handleBulkEditRecord: (value: boolean) => void;
  bulkEditRecord: boolean;
};

const KeyFobMaster = (props: KeyFobMasterProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);

  /* we are performing bulk insert and bulk update */
  const SHOW_BULK_FORM = props.addRecord || props.bulkEditRecord;

  /* STATES */
  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);

  /* 
     true -> bulk entry 
     false -> single entry
  */
  const [isBulkEntry, setIsBulkEntry] = useState(false);

  /* CUSTOM HOOKS */
  const { loading: vehicleTypeListLoading, masterList: vehicleTypeMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.VEHICLE_TYPE_MASTER]);

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  const { bounds, onStart } = useReactDraggable();

  /* HANDLERS */
  const handleBulkEntry = (value: boolean) => {
    setIsBulkEntry(value);
    form.resetFields();
  };

  const handleFinish = (data: any) => {
    let formattedData = { ...data };
    if (SHOW_BULK_FORM && data.key_fob_token) {
      formattedData["keyFobFromRange"] = data.key_fob_token;
      formattedData["keyFobToRange"] = data.key_fob_token;
      delete formattedData["key_fob_token"];
    }
    if (props.addRecord) {
      props.handleSubmit({ ...formattedData, customer_id: masterFilters?.customerId, customer_location_id: masterFilters?.locationId });
    } else {
      props.handleSubmit({ ...formattedData, customer_id: props.rowData?.customer_id, customer_location_id: props.rowData?.customer_location_id });
    }
  };

  /* loading states from multiple hooks added as expression */
  const isOnAddLoading = !(props.addRecord && !vehicleTypeListLoading);
  const isOnEditLoading = !(!props.addRecord && !vehicleTypeListLoading);

  /* EFFECTS */
  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      form.setFieldsValue(props.rowData);
      setFormFieldsLoadedToForm(true);
    }
    // eslint-disable-next-line
  }, [isOnEditLoading]);

  return (
    <Modal
      afterClose={() => form.resetFields()}
      destroyOnClose={true}
      maskClosable={false}
      centered
      title={<div className="modal-container">{`${props.addRecord ? "Add" : "Edit"} KeyFob Master`}</div>}
      open={true}
      onCancel={props.addRecord ? props.handleAddRecord.bind(this, false) : props.bulkEditRecord ? props.handleBulkEditRecord.bind(this, false) : props.handleClearRowIdAndActionType}
      width={"50%"}
      okButtonProps={{
        style: {
          display: "none"
        }
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
    >
      {((props.addRecord && isOnAddLoading) || (!props.addRecord && !formFieldsLoadedToForm)) && (
        <div className={`${styles["textCenter"]}`}>
          <Loading />
        </div>
      )}

      {((props.addRecord && !isOnAddLoading) || (!props.addRecord && formFieldsLoadedToForm)) && (
        <Form
          ref={formRef}
          onValuesChange={(changedValues) => {
            const fieldName = Object.keys(changedValues)[0];
            form.validateFields([fieldName]);
          }}
          className={`${styles["formDiv"]}`}
          {...formItemLayout}
          form={form}
          validateTrigger="onBlur"
          colon={false}
          name="register"
          onFinish={handleFinish}
        >
          {SHOW_BULK_FORM && (
            <Form.Item {...inputLayout} name="isBulkEntry" label="Entry Type">
              <Switch checked={isBulkEntry} onChange={handleBulkEntry} checkedChildren="Bulk Entries" unCheckedChildren="Single Entry" />
            </Form.Item>
          )}

          {SHOW_BULK_FORM && isBulkEntry && (
            <>
              <Form.Item {...inputLayout} name="keyFobFromRange" label="KeyFob From Range" rules={[FormInputRules.requiredFieldInputRule("KeyFob from range")]}>
                <Input
                  name="keyFobFromRange"
                  type="number"
                  min={1}
                  max={9999}
                  onKeyPress={(e) => {
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                {...inputLayout}
                name="keyFobToRange"
                label="KeyFob To Range"
                dependencies={["keyFobFromRange"]}
                rules={[
                  FormInputRules.requiredFieldInputRule("KeyFob to range"),
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const fromRange = getFieldValue("keyFobFromRange");
                      if (+value >= +fromRange) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(ErrorMessageConstants.KEY_FOB_RANGE_ERROR));
                    }
                  })
                ]}
              >
                <Input
                  name="keyFobToRange"
                  type="number"
                  min={1}
                  max={9999}
                  onKeyPress={(e) => {
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </>
          )}

          {(!SHOW_BULK_FORM || !isBulkEntry) && (
            <Form.Item {...inputLayout} name="key_fob_token" label="KeyFob Token" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("key fob token"), FormInputRules.NumberValidationRule()]}>
              <Input name="key_fob_token" type="number" min={1} max={9999} />
            </Form.Item>
          )}

          <Form.Item {...inputLayout} name="vehicle_type_id" label="Vehicle Type" rules={[FormInputRules.requiredFieldSelectRule("vehicle type name")]}>
            <Select className={`${styles["from-Item-selectDiv"]}`} showSearch allowClear listHeight={200} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select vehicle type">
              {vehicleTypeMasterList?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {`${item.vehicle_type_name} - ${item.id}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button htmlType="submit" className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`}>
              {props.saving ? <CircularProgressBar /> : props.addRecord ? "Submit" : "Update"}
            </Button>
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button disabled={props.saving} className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} onClick={props.addRecord ? props.handleAddRecord.bind(this, false) : props.bulkEditRecord ? props.handleBulkEditRecord.bind(this, false) : props.handleClearRowIdAndActionType}>
              Close
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default KeyFobMaster;
